'use strict';

export interface MergeOptions {
    // disable in-place extension of arrays
    array_replace?: boolean;
}

/**
 * Recursively merges the source object(s) into the target object.
 * If both conflicting values are objects, the source object is recursively
 * merged into the target object.
 * If both conflicting values are arrays, the array values from the source are
 * appended to the target array.
 * Updates the target object in place.
 */
export function mergeObjects(target: any, options?: MergeOptions, ...sources: any[]) {
    // todo: work out why for..of doesn't work in tests
    for (let sourceIndex = 0; sourceIndex < sources.length; sourceIndex ++) {
        let source = sources[sourceIndex];
        let properties = Object.getOwnPropertyNames(source);
        for (let attributeIndex = 0; attributeIndex < properties.length; attributeIndex ++) {
            let attribute = properties[attributeIndex];
            let targetValue = target[attribute];
            let sourceValue = source[attribute];
            let newValue;
            // ensure that an array merge always works with copy not reference
            if (Array.isArray(sourceValue) && targetValue === undefined) {
                targetValue = [];
            }
            // two matching arrays => source is appended to target
            if (Array.isArray(sourceValue) && Array.isArray(targetValue)) {
                if (options.array_replace) {
                    targetValue = [];
                }
                for (let innerValue of sourceValue) {
                    if (isMap(innerValue)) {
                        innerValue = mergeObjects({}, options, innerValue);
                    }
                    targetValue.push(innerValue);
                }
                newValue = targetValue;

            // two matching objects => recursively merge source into target
            } else if (isMap(sourceValue) && isMap(targetValue)) {
                newValue = mergeObjects({}, options, targetValue, sourceValue);

            // otherwise, simply replace
            } else {
                newValue = sourceValue;
            }
            target[attribute] = newValue;
        }
    }
    return target;
}

/**
 * Is the given value a map object? i.e. a plain, simple object with keys
 * and values.
 * @param val
 */
function isMap(val: any) {
    return !Array.isArray(val) && typeof val !== 'function' && isObject(val);
}

/**
 * Is the given value an object?
 * (taken from http://stackoverflow.com/questions/8511281/check-if-a-variable-is-an-object-in-javascript)
 */
export function isObject(val: any) : boolean {
    if (val === null) {
        return false;
    }
    return ((typeof val === 'function') || (typeof val === 'object'));
}

/**
 * Loops through the object, returning the list of results of invoking the
 * callback on each key/value pair.
 */
export function forEach(object: any, callback: Function) {
    return Object.keys(object).map(function (key) {
        return callback(key, object[key]);
    });
}
